/**
 * PWAContext
 * Context for PWA installation state and prompt visibility
 */

import { useInstallPromptScheduler } from '@shared/hooks/useInstallPromptScheduler'
import { usePWAInstall } from '@shared/hooks/usePWAInstall'
import { createContext, useContext } from 'react'
import { useTourLifecycle } from './TourLifecycleContext'

interface PWAContextType {
  isInstallable: boolean
  isInstalled: boolean
  isInstalling: boolean
  isPromptVisible: boolean
  promptInstall: () => Promise<boolean>
  dismissPrompt: () => void
  resetSchedule: () => void
}

const PWAContext = createContext<PWAContextType | undefined>(undefined)

export function PWAProvider({ children }: { children: React.ReactNode }) {
  const { isInstallable, isInstalled, isInstalling, promptInstall } = usePWAInstall()
  const { shouldShowPrompt, dismissPrompt, resetSchedule } = useInstallPromptScheduler(
    isInstalled,
    isInstallable
  )
  const { isTourActive } = useTourLifecycle()

  return (
    <PWAContext.Provider
      value={{
        isInstallable,
        isInstalled,
        isInstalling,
        isPromptVisible: shouldShowPrompt && !isTourActive,
        promptInstall,
        dismissPrompt,
        resetSchedule,
      }}
    >
      {children}
    </PWAContext.Provider>
  )
}

export function usePWA() {
  const context = useContext(PWAContext)
  if (!context) {
    throw new Error('usePWA must be used within PWAProvider')
  }
  return context
}
